/** PaletteHost: the ⌘K palette on the kit's CommandPalette. Type for people, views, actions and Labs; at rest the five queues, the people opened lately and All views. The shell owns `open`. */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getTenantInfo } from "../../api/tenantInfo";
import { peopleHref } from "../data/queues";
import { EVENTS_KEY, getEvents } from "../data/events";
import { useQueues } from "../data/useQueues";
import { useQuery } from "../data/useQuery";
import { CommandPalette } from "../kit";
import { loadPerson, personKey } from "../panel/load";
import { getSelection } from "../screens/people/selection";
import { actionEntries, DISTRICT_ENTRIES, eventEntries, filterEntries, grouped, hasPeopleList, LAB_ENTRIES, listScope, pageEntries, personRoute, queueEntries, restEntries, routeFor, VIEW_ENTRIES, type ActionId, type Entry } from "./entries";
import { front, readRecent, writeRecent } from "./recent";
import { usePeopleSearch } from "./usePeopleSearch";

export interface PaletteHostProps {
  open: boolean;
  onClose: () => void;
  /** The shell's dialogs. An action appears only when its callback is given. */
  onFeedback?: () => void;
  onHelp?: () => void;
}

/** Press the People screen's own button, so the palette never holds a copy of the rows. After a hop to People it is still loading: wait for it. */
function pressOnPage(label: RegExp, tries = 30): void {
  const button = Array.from(document.querySelectorAll<HTMLButtonElement>("main button")).find((b) => label.test(b.textContent?.trim() ?? ""));
  if (button && !button.disabled) button.click();
  else if (tries > 0) window.setTimeout(() => pressOnPage(label, tries - 1), 100);
}

export function PaletteHost({ open, onClose, onFeedback, onHelp }: PaletteHostProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [query, setQuery] = useState("");
  const [allViews, setAllViews] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  // Each opening starts clean; resetting here, not on close, keeps the list steady while it fades out.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery("");
      setAllViews(false);
    }
  }

  // The people opened lately, from the palette or anywhere else: the sheet's `?person=` is the one place to see it.
  const [recent, setRecent] = useState(readRecent);
  const personId = Number(new URLSearchParams(search).get("person")) || null;
  const known = recent.find((r) => r.id === personId);
  // The sheet's own request: a deep link to a person asks for them once.
  const opened = useQuery(personId !== null && !known ? personKey(personId) : null, () => loadPerson(personId ?? 0));
  const name = known?.name ?? opened.data?.person.identity.display_name;
  if (personId !== null && name && recent[0]?.id !== personId) setRecent(front(recent, { id: personId, name }));
  useEffect(() => writeRecent(recent), [recent]);

  const { rows: people, settled, retry } = usePeopleSearch(open ? query : "");
  const events = useQuery(open ? EVENTS_KEY : null, getEvents);
  const tenant = useQuery(open ? "v2:tenant" : null, getTenantInfo);
  const capabilities = tenant.data?.capabilities;
  // Asks only while open; the sidebar's own fetch has the answer cached.
  const queues = useQueues(open);

  const views = [...VIEW_ENTRIES, ...eventEntries(events.data?.results), ...DISTRICT_ENTRIES];
  const actions = actionEntries({ capabilities, feedback: !!onFeedback, help: !!onHelp, scope: listScope(pathname, search, events.data?.results) });
  const recents: Entry[] = recent.map((r) => ({ key: `recent:${r.id}`, group: "recent", label: r.name, target: { kind: "person", id: r.id } }));
  const entries: readonly Entry[] = query.trim()
    ? [...people, ...filterEntries([...views, ...pageEntries({ pathname, search, capabilities })], query), ...filterEntries(actions, query), ...filterEntries(LAB_ENTRIES, query)]
    : allViews
      ? views
      : restEntries(queueEntries(queues.list), recents);

  const run: Record<ActionId, () => void> = {
    "copy-emails": () => pressOnPage(/^Copy emails/),
    "export-csv": () => pressOnPage(/^Export CSV/),
    feedback: () => onFeedback?.(),
    help: () => onHelp?.(),
  };

  const choose = (key: string) => {
    const entry = entries.find((e) => e.key === key);
    const target = entry?.target;
    if (!entry || !target) return;
    if (target.kind === "all-views") return setAllViews(true);
    if (target.kind === "retry-search") return retry();
    if (target.kind === "person") {
      setRecent(front(recent, { id: target.id, name: entry.label }));
      onClose();
      void navigate(personRoute(target.id, { pathname, search }, getSelection().ids.includes(target.id)));
      return;
    }
    const route = routeFor(target);
    if (route) {
      onClose();
      void navigate(route);
    } else if (target.kind === "action") {
      onClose();
      // The two list actions press the page's own buttons, so from a page with no list of people they go to Everyone first.
      if ((target.id === "copy-emails" || target.id === "export-csv") && !hasPeopleList(pathname)) void navigate(peopleHref("all"));
      // After the close, so focus has returned and the page's own button answers a plain click.
      window.setTimeout(run[target.id], 0);
    }
  };

  return (
    <CommandPalette
      open={open}
      onClose={onClose}
      label="Search people, views and actions"
      query={query}
      onQueryChange={setQuery}
      settled={settled}
      onChoose={choose}
      groups={grouped(entries).map((group) => ({
        id: group.id,
        label: group.label,
        items: group.items.map((entry) => ({ key: entry.key, label: entry.label, hint: entry.hint, match: entry.match, person: entry.target.kind === "person" })),
      }))}
    />
  );
}
